import { useEffect, useRef, useState, memo, useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import Sketch from '@uiw/react-color-sketch';
import { TAILWIND_PALETTE } from '../canvas/lib/colorPaletteConstants';

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  iconColor?: string;
  color?: string;       // text color override (e.g. '#dc2626' for red)
  hoverBg?: string;     // hover background override
  separator?: boolean;  // show separator BEFORE this item
  disabled?: boolean;
  children?: ContextMenuItem[];  // submenu items (renders as hover-expand)
  onClick?: () => void;  // optional when item has children
  swatchColor?: string;  // renders a colored square indicator
  renderChildrenAsGrid?: boolean;  // renders children as a swatch grid instead of a list
  /** Opens an inline Sketch color picker instead of onClick */
  colorPickerConfig?: {
    currentColor: string;
    onSelectColor: (color: string) => void;
    enableAlpha?: boolean;
  };
  /** Renders an inline number input instead of a clickable button */
  inlineNumberInput?: {
    currentValue: number;
    onChangeValue: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
  };
}

export interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

function ContextMenuInner({ x, y, items, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Viewport-clamped position
  const adjustedX = Math.min(x, window.innerWidth - 200);
  const adjustedY = Math.min(y, window.innerHeight - items.length * 36 - 20);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Delay to avoid immediate close from the triggering contextmenu event
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('keydown', handleKeyDown);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: adjustedX,
        top: adjustedY,
        zIndex: 9999,
      }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]"
    >
      {items.map(item => (
        <ContextMenuItemRow key={item.id} item={item} onClose={onClose} />
      ))}
    </div>
  );
}

function ContextMenuItemRow({ item, onClose }: { item: ContextMenuItem; onClose: () => void }) {
  const [hovered, setHovered] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [numberInputValue, setNumberInputValue] = useState(() =>
    item.inlineNumberInput ? String(item.inlineNumberInput.currentValue) : ''
  );
  const Icon = item.icon;
  const hasChildren = !!(item.children && item.children.length > 0);
  const hasColorPicker = !!item.colorPickerConfig;
  const hasSubmenu = hasChildren || hasColorPicker;

  const handleEnter = () => {
    clearTimeout(leaveTimer.current);
    setHovered(true);
  };
  const handleLeave = () => {
    leaveTimer.current = setTimeout(() => setHovered(false), hasSubmenu ? 150 : 0);
  };

  // Determine if submenu should open to the left
  let openLeft = false;
  if (hasSubmenu && hovered && wrapRef.current) {
    const rect = wrapRef.current.getBoundingClientRect();
    if (rect.right + 260 > window.innerWidth) openLeft = true;
  }

  // Inline number input mode
  if (item.inlineNumberInput) {
    const inp = item.inlineNumberInput;
    const handleSubmit = () => {
      const num = parseFloat(numberInputValue);
      if (!isNaN(num)) {
        const min = inp.min ?? 0;
        const max = inp.max ?? Infinity;
        const clamped = Math.max(min, Math.min(max, num));
        inp.onChangeValue(clamped);
        onClose();
      }
    };

    return (
      <>
        {item.separator && (
          <div className="h-px bg-gray-100 mx-1.5 my-1" />
        )}
        <div
          className="flex items-center gap-2 px-3 py-1.5 text-xs"
          style={{ color: '#374151' }}
        >
          <span className="flex-1" style={{ whiteSpace: 'nowrap' }}>{item.label}</span>
          <input
            type="number"
            value={numberInputValue}
            onChange={e => setNumberInputValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSubmit();
              if (e.key === 'Escape') onClose();
              e.stopPropagation();
            }}
            onMouseDown={e => e.stopPropagation()}
            min={inp.min}
            max={inp.max}
            step={inp.step}
            style={{
              width: 48,
              height: 22,
              padding: '0 4px',
              fontSize: 11,
              border: '1px solid #d1d5db',
              borderRadius: 3,
              outline: 'none',
              backgroundColor: '#fff',
              textAlign: 'right',
            }}
          />
          {inp.unit && (
            <span style={{ fontSize: 10, color: '#6b7280' }}>{inp.unit}</span>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {item.separator && (
        <div className="h-px bg-gray-100 mx-1.5 my-1" />
      )}
      <div
        ref={wrapRef}
        style={{ position: 'relative' }}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <button
          disabled={item.disabled}
          onClick={() => {
            if (!item.disabled && item.onClick && !hasColorPicker) {
              item.onClick();
              onClose();
            }
          }}
          className="flex items-center gap-2 w-full px-3 py-1.5 text-left text-xs disabled:opacity-40 disabled:cursor-default"
          style={{
            color: item.color ?? '#374151',
            backgroundColor: hovered && !item.disabled ? (item.hoverBg ?? '#f3f4f6') : 'transparent',
            borderRadius: 4,
          }}
        >
          {Icon && <Icon size={13} style={{ color: item.iconColor ?? item.color ?? '#6b7280' }} />}
          {item.swatchColor && (
            <span style={{
              width: 14,
              height: 14,
              borderRadius: 3,
              backgroundColor: item.swatchColor,
              border: '1px solid #d1d5db',
              flexShrink: 0,
              display: 'inline-block',
            }} />
          )}
          <span className="flex-1">{item.label}</span>
          {hasChildren && <span className="text-gray-400 text-[10px] ml-2">{item.renderChildrenAsGrid ? '\u25A6' : '\u203A'}</span>}
          {hasColorPicker && <span className="text-gray-400 text-[10px] ml-2">{'\u25A6'}</span>}
        </button>

        {/* Color picker submenu */}
        {hasColorPicker && hovered && (
          <InlineColorPicker
            config={item.colorPickerConfig!}
            openLeft={openLeft}
            onSelectColor={(color) => {
              item.colorPickerConfig!.onSelectColor(color);
            }}
          />
        )}

        {/* Normal submenu */}
        {hasChildren && hovered && !hasColorPicker && (
          item.renderChildrenAsGrid ? (
            <div
              style={{
                position: 'absolute',
                top: -4,
                [openLeft ? 'right' : 'left']: '100%',
                zIndex: 10000,
              }}
              className="bg-white border border-gray-200 rounded-lg shadow-lg p-2"
            >
              {/* Swatch grid */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, maxWidth: 210 }}>
                {item.children!.filter(c => c.swatchColor).map(child => (
                  <button
                    key={child.id}
                    onClick={() => { child.onClick?.(); onClose(); }}
                    title={child.label}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 4,
                      backgroundColor: child.swatchColor,
                      border: child.label.startsWith('\u25CF') ? '2px solid #2563eb' : '1px solid #d1d5db',
                      cursor: 'pointer',
                      padding: 0,
                      flexShrink: 0,
                      boxShadow: child.label.startsWith('\u25CF') ? '0 0 0 1.5px #fff inset' : 'none',
                    }}
                  />
                ))}
              </div>
              {/* Non-swatch items below the grid */}
              {item.children!.filter(c => !c.swatchColor).length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {item.children!.filter(c => !c.swatchColor).map(child => (
                    <ContextMenuItemRow key={child.id} item={child} onClose={onClose} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                position: 'absolute',
                top: -4,
                [openLeft ? 'right' : 'left']: '100%',
                zIndex: 10000,
              }}
              className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]"
            >
              {item.children!.map(child => (
                <ContextMenuItemRow key={child.id} item={child} onClose={onClose} />
              ))}
            </div>
          )
        )}
      </div>
    </>
  );
}

// ── Inline Sketch Color Picker (opens as submenu) ──

function InlineColorPicker({ config, openLeft, onSelectColor }: {
  config: { currentColor: string; enableAlpha?: boolean };
  openLeft: boolean;
  onSelectColor: (color: string) => void;
}) {
  const presetColors = useMemo(() => [...TAILWIND_PALETTE], []);

  return (
    <div
      style={{
        position: 'absolute',
        top: -4,
        [openLeft ? 'right' : 'left']: '100%',
        zIndex: 10000,
      }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Sketch
        color={config.currentColor || '#374151'}
        disableAlpha={!config.enableAlpha}
        presetColors={presetColors}
        onChange={(color) => {
          if (config.enableAlpha && color.rgba.a !== undefined && color.rgba.a < 1) {
            onSelectColor(`rgba(${color.rgba.r}, ${color.rgba.g}, ${color.rgba.b}, ${color.rgba.a})`);
          } else {
            onSelectColor(color.hex);
          }
        }}
        style={{ boxShadow: 'none', border: 'none' }}
      />
    </div>
  );
}

export const ContextMenu = memo(ContextMenuInner);
